// styled.ts — styled-components y keyframes del chat de generación de Novah.
// Qué hace: define todos los styled-components (chat, burbujas, widgets, pickers)
//   y las animaciones del chat. Sin lógica.
// Qué exporta: keyframes (fadeIn, dotBounce) + todos los styled-components.
// Quién lo usa: NovahGenerateChat y los widgets de widgets/.

import styled, { keyframes } from 'styled-components';

// ─── Animations ─────────────────────────────────────────────────────────────

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

// ─── Chat Styled Components ─────────────────────────────────────────────────

export const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px 2px;
  scroll-behavior: smooth;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 2px; }
`;

export const MsgRow = styled.div<{ $from: 'novah' | 'user' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  justify-content: ${({ $from }) => ($from === 'user' ? 'flex-end' : 'flex-start')};
  animation: ${fadeIn} 0.3s ease-out;
`;

export const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.25);
  img { width: 100%; height: 100%; object-fit: cover; }
`;

export const Bubble = styled.div<{ $from: 'novah' | 'user' }>`
  max-width: 85%;
  padding: 8px 12px;
  border-radius: ${({ $from }) => ($from === 'novah' ? '4px 12px 12px 12px' : '12px 4px 12px 12px')};
  font-size: 13px;
  line-height: 1.5;
  background: ${({ $from, theme }) => ($from === 'novah' ? theme.colors.background : theme.colors.primary)};
  color: ${({ $from, theme }) => ($from === 'novah' ? theme.colors.text : '#fff')};
  border: ${({ $from, theme }) => ($from === 'novah' ? theme.borders.thin : 'none')};
  strong { font-weight: 600; }
`;

export const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 6px 4px;
  span {
    width: 6px; height: 6px; border-radius: 50%;
    background: ${({ theme }) => theme.colors.textMuted};
    animation: ${dotBounce} 1.2s infinite;
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
`;

// ─── Widget Styled Components ───────────────────────────────────────────────

export const WidgetWrap = styled.div`
  animation: ${fadeIn} 0.3s ease-out;
  margin: 4px 0;
`;

export const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

export const PlatformBtn = styled.button<{ $active: boolean }>`
  padding: 8px 10px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.text)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const ConfirmBtn = styled.button`
  width: 100%;
  padding: 8px 12px;
  margin-top: 6px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.primaryHover}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const CountGrid = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export const CountBtn = styled.button<{ $active: boolean }>`
  padding: 6px 16px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.surface)};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.text)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const PromptInput = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-family: inherit;
  resize: none;
  min-height: 50px;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const SkipBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  cursor: pointer;
  margin-top: 4px;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

export const ContactForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ContactInput = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-family: inherit;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const BackBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 12px;
  cursor: pointer;
  padding: 4px 0;
  &:hover { color: ${({ theme }) => theme.colors.primary}; }
`;

export const StyleOption = styled.button<{ $active: boolean; $locked?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 8px;
  border: 1px solid ${({ $active, $locked, theme }) => ($locked ? theme.colors.border : $active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ $active, $locked, theme }) => ($locked ? theme.colors.background : $active ? 'rgba(59,130,246,0.08)' : theme.colors.surface)};
  cursor: ${({ $locked }) => ($locked ? 'pointer' : 'pointer')};
  transition: all 0.15s ease;
  opacity: ${({ $locked }) => ($locked ? 0.55 : 1)};
  position: relative;
  &:hover { border-color: ${({ $locked, theme }) => ($locked ? theme.colors.border : theme.colors.primary)}; }
`;

export const LockBadge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: 10px;
  background: rgba(0,0,0,0.08);
  border-radius: 8px;
  padding: 1px 5px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-weight: 700;
`;

export const PlanTag = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
`;

export const StyleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
`;

// Picker dedicado a los AI-styled posters: thumbnails 3:4 reales (no icon+label).
// Va en grilla compacta + scroll con tope de alto, para que la lista no explote
// el chat cuando hay muchos estilos (built-in + custom).
export const PosterStylePicker = styled.div`
  max-height: 290px;
  overflow-y: auto;
  margin: 2px 0;
  padding-right: 4px;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 3px; }
`;

export const PosterStyleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(92px, 1fr));
  gap: 6px;
`;

export const StyleIcon = styled.span`
  font-size: 20px;
  margin-bottom: 4px;
`;

export const StyleLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

export const SummaryCard = styled.div`
  background: ${({ theme }) => theme.colors.background};
  border: ${({ theme }) => theme.borders.thin};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  strong { color: ${({ theme }) => theme.colors.text}; }
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 3px;
  overflow: hidden;
  margin: 6px 0;
`;

export const ProgressFill = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => $pct}%;
  height: 100%;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.3s ease;
`;

// ─── Multi-tema (TopicsStep) ────────────────────────────────────────────────
// Una fila por tema guardado: texto editable + cupo + quitar. En 390px entran
// los tres (input flexible, select fijo, botón chico) sin desbordar el rail.
export const TopicRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

export const TopicCountSelect = styled.select`
  flex: none;
  width: 56px;
  padding: 7px 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-family: inherit;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`;

export const RemoveBtn = styled.button`
  flex: none;
  width: 26px;
  height: 30px;
  background: none;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  &:hover { color: ${({ theme }) => theme.colors.error}; border-color: ${({ theme }) => theme.colors.border}; }
`;

// La lista se acota y scrollea: con 10 temas, sin esto el botón Continue se va
// abajo del fold del rail de Novah.
export const TopicList = styled.div`
  max-height: 210px;
  overflow-y: auto;
  margin-bottom: 6px;
  padding-right: 4px;
  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.border}; border-radius: 3px; }
`;

// Contador vivo contra el presupuesto del paso de cantidad. El ámbar es el mismo
// registro que el aviso de formato del resumen: informa, nunca bloquea.
export const CounterLine = styled.div<{ $tone: 'neutral' | 'warn' }>`
  font-size: 11px;
  line-height: 1.5;
  margin-top: 6px;
  color: ${({ theme, $tone }) => ($tone === 'warn' ? '#b45309' : theme.colors.textMuted)};
`;

export const TopicDraftRow = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
`;

// "Add topic" NO puede ser el mismo botón lleno que "Continue": mirando la
// captura quedaban dos CTA azules idénticos uno encima del otro y no se sabía
// cuál era el que avanza. Add es secundario; el primario es Continue.
export const AddTopicBtn = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px dashed ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: ${({ theme }) => theme.colors.surfaceHover}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; border-style: solid; color: ${({ theme }) => theme.colors.textMuted}; border-color: ${({ theme }) => theme.colors.border}; }
`;

// Separa los temas ya guardados de la caja del siguiente: sin esto se leían
// como una sola lista de tres campos y no se entendía cuál era el nuevo.
export const TopicsDivider = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: 8px 0 2px;
`;
